import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { lazy, Suspense } from "react";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LazyLoadErrorBoundary } from "@/components/LazyLoadErrorBoundary";
import { LoadingState } from "@/components/LoadingState";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { ScrollToTop } from "./components/ScrollToTop";

// Wrap BrowserRouter with Sentry routing instrumentation
const SentryRoutes = Sentry.withSentryRouting(Routes);

// Lazy load page components
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const GuestRegistration = lazy(() => import("./pages/GuestRegistration"));
const DemoGuestRegistration = lazy(() => import("./pages/DemoGuestRegistration"));
const PartySize = lazy(() => import("./pages/PartySize"));
const GuestList = lazy(() => import("./pages/GuestList"));
const RegistrationConfirm = lazy(() => import("./pages/RegistrationConfirm"));
const RegistrationReturn = lazy(() => import("./pages/RegistrationReturn"));
const RegistrationSuccess = lazy(() => import("./pages/RegistrationSuccess"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PropertiesList = lazy(() => import("./pages/PropertiesList"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const StatecDashboard = lazy(() => import("./pages/StatecDashboard"));
const StatecPropertyDetail = lazy(() => import("./pages/StatecPropertyDetail"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MigrateImages = lazy(() => import("./pages/admin/MigrateImages"));
const StatecSubmissions = lazy(() => import("./pages/admin/StatecSubmissions"));
const Bookings = lazy(() => import("./pages/admin/Bookings"));
const Messages = lazy(() => import("./pages/admin/Messages"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const AIAnalytics = lazy(() => import("./pages/admin/AIAnalytics"));
const Operations = lazy(() => import("./pages/admin/Operations"));
const StayAgent = lazy(() => import("./pages/admin/StayAgent"));
const RLHFAdmin = lazy(() => import("./pages/admin/RLHFAdmin"));

// Initialize QueryClient with error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Loading fallback component - elegant spinner for page transitions
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="h-12 w-12 rounded-full border-4 border-muted" />
        <div className="absolute inset-0 h-12 w-12 rounded-full border-4 border-transparent border-t-primary animate-spin" />
      </div>
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <ComparisonProvider>
        <TooltipProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <LazyLoadErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <SentryRoutes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/register" element={<GuestRegistration />} />
                  <Route path="/register/demo" element={<DemoGuestRegistration />} />
                  <Route path="/register/party" element={<PartySize />} />
                  <Route path="/register/guests" element={<GuestList />} />
                  <Route path="/register/confirm" element={<RegistrationConfirm />} />
                  <Route path="/register/return" element={<RegistrationReturn />} />
                  <Route path="/register/success" element={<RegistrationSuccess />} />
                  
                  {/* Protected Admin Routes */}
                  <Route element={
                    <ProtectedRoute requireAdmin>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }>
                    <Route
                      path="/dashboard"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Dashboard />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/analytics"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Analytics />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/properties" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <PropertiesList />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route
                      path="/properties/statec" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <PropertiesList />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route
                      path="/properties/soumissions" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <PropertiesList />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/properties/:id" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <PropertyDetail />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/properties/statec/:id" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <StatecPropertyDetail />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    {/* Legacy routes - redirect to new paths */}
                    <Route 
                      path="/statec" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <PropertiesList />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/statec/:id" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <StatecPropertyDetail />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/profile" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <UserProfile />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/admin/migrate-images" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <MigrateImages />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/submissions" 
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <StatecSubmissions />
                          </Suspense>
                        </ErrorBoundary>
                      } 
                    />
                    <Route
                      path="/bookings"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Bookings />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/messages"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Messages />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/stay-agent"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <StayAgent />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/operations"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Operations />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/ai-analytics"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AIAnalytics />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/admin/rlhf"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <RLHFAdmin />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                  </Route>
                  
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </SentryRoutes>
                </Suspense>
              </LazyLoadErrorBoundary>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </ComparisonProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
